/**
 * TenantSwitcher — top-bar pill that opens a dropdown of the tenants
 * the calling user has membership in. Selecting a tenant POSTs to
 * /me/tenant which sets the signed `sage-active-tenant` cookie, then
 * we reload so every component re-fetches against the new context.
 *
 * When the user has only one tenant we render a passive pill (same
 * visual as today's tenant badge). The dropdown only appears when
 * there's something to switch to.
 */

import { useEffect, useRef, useState } from 'react';
import type { JSX } from 'react';
import { apiFetch } from '../api/client.js';

interface TenantSummary {
  tenantId: string;
  tenantName: string;
  roles: ReadonlyArray<string>;
}

interface TenantsResponse {
  activeTenantId: string;
  email: string;
  tenants: ReadonlyArray<TenantSummary>;
}

export function TenantSwitcher({
  token,
  tenantId,
  tenantShort,
}: {
  token: string;
  tenantId: string;
  tenantShort: string;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<TenantsResponse | null>(null);
  const [err, setErr] = useState<string>('');
  const [switching, setSwitching] = useState<string>('');
  const ref = useRef<HTMLDivElement | null>(null);

  // Fetch the list eagerly on mount so the trigger pill can show the
  // *actively-active* tenant name (which may differ from the JWT
  // default if a cookie override is in effect). Without this, the
  // label would show the JWT default after a reload even though the
  // server is honoring the override.
  useEffect(() => {
    setLoading(true);
    setErr('');
    apiFetch<TenantsResponse>('/me/tenants', token)
      .then((r) => setData(r))
      .catch((e) => setErr(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [token]);

  // Close on outside click / Esc.
  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent): void {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onEsc(e: KeyboardEvent): void {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  async function chooseTenant(target: TenantSummary): Promise<void> {
    // Don't no-op when the clicked tenant equals the JWT-default
    // (`tenantId` prop). The user may currently have a cookie
    // override active that pinned them to a different tenant — in
    // that case, clicking the JWT-default should clear the
    // override and bring them back. Either way: always POST,
    // server is idempotent.
    //
    // Skip only when clicking the truly-active tenant (the one
    // currently reflected in /me/tenants.activeTenantId).
    if (target.tenantId === data?.activeTenantId) {
      setOpen(false);
      return;
    }
    setSwitching(target.tenantId);
    setErr('');
    try {
      await apiFetch('/me/tenant', token, {
        method: 'POST',
        body: JSON.stringify({ tenantId: target.tenantId }),
      });
      // Hard reload so every cached query / component refetches
      // against the new tenant context.
      window.location.reload();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
      setSwitching('');
    }
  }

  async function clearOverride(): Promise<void> {
    setSwitching('clear');
    setErr('');
    try {
      await apiFetch('/me/tenant', token, { method: 'DELETE' });
      window.location.reload();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
      setSwitching('');
    }
  }

  // Display the active tenant's friendly name when we've loaded it,
  // otherwise fall back to the short id from the JWT. Helps users
  // who have multiple tenants distinguish them at a glance instead
  // of memorizing uuid prefixes.
  const activeName =
    data?.tenants.find((t) => t.tenantId === (data?.activeTenantId ?? tenantId))?.tenantName ??
    null;

  return (
    <div className="tenant-switcher" ref={ref}>
      <button
        type="button"
        className="tenant-switcher-trigger"
        title={`Tenant ${tenantId} — click to switch`}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <span
          className="badge-dot"
          style={{ color: 'var(--color-success-fg, #10b981)' }}
          aria-hidden="true"
        />
        <span className="tenant-switcher-label">{activeName ?? tenantShort}</span>
        <svg className="tenant-switcher-caret" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path
            d="M4 6l4 4 4-4"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {open && (
        <div className="tenant-switcher-pop" role="menu">
          <div className="tenant-switcher-header">
            {data?.email ? <span title={data.email}>{data.email}</span> : 'Tenants'}
          </div>
          {loading && <div className="tenant-switcher-empty">Loading…</div>}
          {err && <div className="tenant-switcher-error">{err}</div>}
          {!loading && !err && data && data.tenants.length === 0 && (
            <div className="tenant-switcher-empty">No tenants available.</div>
          )}
          {!loading && !err && data && data.tenants.length > 0 && (
            <>
              {data.tenants.map((t) => {
                const isActive = t.tenantId === data.activeTenantId;
                const isBusy = switching === t.tenantId;
                return (
                  <button
                    key={t.tenantId}
                    type="button"
                    role="menuitem"
                    className={`tenant-switcher-item${isActive ? ' is-active' : ''}`}
                    onClick={() => void chooseTenant(t)}
                    disabled={isBusy || isActive}
                    title={t.tenantId}
                  >
                    <span className="tenant-switcher-item-name">{t.tenantName}</span>
                    {isActive && <span className="tenant-switcher-item-check">✓</span>}
                  </button>
                );
              })}
              <div className="tenant-switcher-divider" />
              <button
                type="button"
                role="menuitem"
                className="tenant-switcher-item tenant-switcher-item--reset"
                onClick={() => void clearOverride()}
                disabled={switching === 'clear'}
                title="Forget the manual selection. Sage will use the tenant your sign-in token issued."
              >
                Use sign-in default
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
